import { useMutation } from "@tanstack/react-query";
import ApiErrorBanner from "components/ApiErrorBanner";
import Button from "components/ui/Button";
import Text from "components/ui/Text";
import { UserContext } from "contexts/UserContext";
import { useRequiredContext } from "contexts/utils";
import Wordmark from "components/Wordmark";
import { routes } from "pages/routes";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { shutdownIntercom } from "services/intercom";
import { logError } from "services/logging";
import { trackEvent } from "services/productAnalytics";
import { markUrlSecretSpent, urlSecret } from "services/payments/urlSecrets";
import { consumeSignInLink, isSignInLinkInvalid } from "services/users/auth";
import { useDocumentTitle } from "utils/useDocumentTitle";
import type { SignInLocationState } from "./SignIn";
import signInStyles from "./SignIn.module.scss";
import styles from "./SignInLink.module.scss";

type ContinueArgs = {
  token: string;
  // Read at the click, since the user in context may change under the mutation.
  replacesSession: boolean;
};

/**
 * Where the emailed sign-in link lands. The token is posted on the Continue
 * click, never on load: corporate mail security opens links and runs the page,
 * and would spend the link before the person does. The page names no account,
 * since nothing it can trust says whose the link is.
 */
export default function SignInLinkPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = urlSecret("token");
  const { user, refreshCurrentUser } = useRequiredContext(UserContext);
  const isSignedIn = !!user;

  useDocumentTitle(t("Auth.signInLink.pageTitle"));

  // The token is single-use, so once the server has taken it a retry repeats only
  // what follows.
  const hasConsumed = useRef(false);
  const continueMutation = useMutation({
    mutationFn: async ({ token: linkToken, replacesSession }: ContinueArgs) => {
      if (!hasConsumed.current) {
        await consumeSignInLink({ token: linkToken });
        hasConsumed.current = true;
        markUrlSecretSpent("token");
      }
      if (replacesSession) {
        await shutdownIntercom().catch(logError);
      } else {
        await refreshCurrentUser();
      }
    },
    onSuccess: (_result, { replacesSession }) => {
      trackEvent("sign_in_success");
      if (replacesSession) {
        // A full load: per-load state (the query cache, the library store) and
        // Intercom's conversation belong to the previous user, and a navigation
        // resets none of them.
        window.location.replace(routes.root.path());
      } else {
        navigate(routes.root.path(), { replace: true });
      }
    },
    onError: error => {
      if (isSignInLinkInvalid(error)) {
        markUrlSecretSpent("token");
      }
    },
  });

  const continueSignIn = () => {
    if (token) {
      continueMutation.mutate({ token, replacesSession: isSignedIn });
    }
  };

  // No token and a session: a reload after a Continue that went through.
  if (!token && isSignedIn) {
    return <Navigate to={routes.root.path()} replace />;
  }

  const isLinkInvalid = !token || isSignInLinkInvalid(continueMutation.error);

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <Wordmark className={styles.logo} />

        {isLinkInvalid ? (
          <div className={styles.panel}>
            <Text as="h1" variant="heading-4" className={styles.title}>
              {t("Auth.signInLink.expiredTitle")}
            </Text>
            <Text as="p" variant="body-2" className={styles.description}>
              {t("Auth.signInLink.expiredDescription")}
            </Text>
            <Button
              as="link"
              size="large"
              className={signInStyles.button}
              to={isSignedIn ? routes.signOut.path() : routes.public.signIn.path()}
              state={{ signInLinkFormOpen: true } satisfies SignInLocationState}
            >
              {t("Auth.signInLink.requestNew")}
            </Button>
          </div>
        ) : (
          <div className={styles.panel}>
            <Text as="h1" variant="heading-4" className={styles.title}>
              {t(
                isSignedIn
                  ? "Auth.signInLink.signedInTitle"
                  : "Auth.signInLink.continueTitle"
              )}
            </Text>
            <Text as="p" variant="body-2" className={styles.description}>
              {t(
                isSignedIn
                  ? "Auth.signInLink.signedInDescription"
                  : "Auth.signInLink.continueDescription"
              )}
            </Text>
            <ApiErrorBanner error={continueMutation.error} />
            <Button
              size="large"
              className={signInStyles.button}
              loading={continueMutation.isPending}
              onClick={continueSignIn}
            >
              {t("Common.actions.continue")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
